import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useMutation } from '@tanstack/react-query';
import { publicApi, getPublicApiErrorMessage } from '../../services/publicApi';

interface LeadMagnetWidgetProps {
  slug: string;
  headline: string;
  subheadline: string;
  buttonText: string;
  primaryColor: string;
}

export const LeadMagnetWidget = ({
  slug,
  headline,
  subheadline,
  buttonText,
  primaryColor,
}: LeadMagnetWidgetProps) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    challengeText: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: typeof formData) => publicApi.submitLead(slug, data),
    onSuccess: () => setSubmitted(true),
  });

  const handleChange = (field: keyof typeof formData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  if (submitted) {
    return (
      <Box
        component="section"
        id="lead-magnet"
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: 'grey.50',
          borderTop: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              textAlign: 'center',
              py: 4,
            }}
          >
            <CheckCircleIcon
              sx={{
                fontSize: 64,
                color: 'success.main',
                mb: 2,
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'grey.900',
                mb: 2,
              }}
            >
              You're all set!
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'grey.600',
                fontSize: '1.1rem',
              }}
            >
              Check your email for your personalized action plan.
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="section"
      id="lead-magnet"
      sx={{
        py: { xs: 8, md: 10 },
        bgcolor: 'grey.50',
        borderTop: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      <Container maxWidth="sm">
        <Typography
          variant="h3"
          component="h2"
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            mb: 2,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            color: 'grey.900',
          }}
        >
          {headline}
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: 'grey.600',
            textAlign: 'center',
            mb: 4,
            fontSize: '1.1rem',
            lineHeight: 1.6,
          }}
        >
          {subheadline}
        </Typography>

        {mutation.error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {getPublicApiErrorMessage(mutation.error)}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            bgcolor: 'white',
            p: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label="First Name *"
              value={formData.firstName}
              onChange={handleChange('firstName')}
              fullWidth
              size="medium"
            />
            <TextField
              label="Last Name"
              value={formData.lastName}
              onChange={handleChange('lastName')}
              fullWidth
              size="medium"
            />
          </Box>

          <TextField
            label="Email *"
            type="text"
            value={formData.email}
            onChange={handleChange('email')}
            fullWidth
            sx={{ mb: 2 }}
            size="medium"
          />

          <TextField
            label="What tasks or challenges are taking up too much of your time? *"
            value={formData.challengeText}
            onChange={handleChange('challengeText')}
            multiline
            rows={4}
            fullWidth
            sx={{ mb: 3 }}
            placeholder="Example: I spend hours every week on email management, scheduling, and following up with clients. I also struggle to keep my social media consistent..."
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={mutation.isPending}
            sx={{
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: primaryColor,
              boxShadow: `0 4px 14px ${primaryColor}40`,
              '&:hover': {
                bgcolor: primaryColor,
                filter: 'brightness(0.9)',
              },
              '&:disabled': {
                bgcolor: primaryColor,
                opacity: 0.7,
              },
            }}
          >
            {mutation.isPending ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              buttonText
            )}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
